import React, { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { Avatar } from '@/components/ui/Avatar'
import { ChevronLeft, ChevronRight, LogOut, MoreHorizontal, Phone, Search, Settings } from 'lucide-react'
import { useCallStore } from '@/stores/callStore'
import { displayName } from './account'
import { WorkspaceMark } from './WorkspaceMark'
import { BusinessName } from './BusinessName'
import { SHORTCUT_LABEL } from '@/lib/platform'
import { NotificationDropdown } from './NotificationDropdown'
import { navItems, visibleNavItems, type NavItem } from './navItems'
import { usePermissions } from '@/hooks/usePermission'
import { PRODUCT_NAME } from '@/lib/brand'
import { useWorkspace } from '@/lib/workspace'
import { AssistantButton } from '@/components/Assistant/AssistantPanel'



function isActivePath(pathname: string, path: string) {
  return pathname === path || (path !== '/dashboard' && pathname.startsWith(path + '/'))
}

function NavRow({ item, collapsed, badge }: { item: NavItem; collapsed: boolean; badge: number }) {
  const location = useLocation()
  const active = isActivePath(location.pathname, item.path)
  const Icon = item.icon

  return (
    <NavLink
      to={item.path}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex items-center gap-[11px] h-9 coarse:h-11 px-3 rounded-xl text-[13.5px] font-medium transition-colors duration-200',
        active
          ? 'nav-pill-active text-white shadow-[0_4px_12px_-6px_rgba(0,0,0,0.5)]'
          : 'text-white/[0.66] hover:text-white hover:bg-white/[0.06]',
        collapsed && 'justify-center px-0',
      )}
      title={collapsed ? item.label : undefined}
    >
      <Icon className={cn('w-4 h-4 shrink-0', active ? 'text-white' : 'text-white/[0.62] group-hover:text-white/80')} />

      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {badge > 0 && (
            // White pill: an unread count is "needs you now", the same signal
            // as the white primary button. Pastels stay reserved for phases.
            <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold tabular-nums bg-white text-black">
              {badge}
            </span>
          )}
        </>
      )}

      {collapsed && badge > 0 && (
        <span className="absolute top-1.5 right-2.5 w-2 h-2 bg-white rounded-full shadow-[0_0_0_2px_#000]" />
      )}

      {collapsed && (
        <span className="absolute left-full ml-2 px-2 py-1 glass rounded-md text-xs text-ink-100 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[21]">
          {item.label}
        </span>
      )}
    </NavLink>
  )
}

/**
 * Phone, as a sidebar row (UX-111). It used to be a 56px button floating at
 * the bottom-right of every desktop page, where it sat on row actions, money
 * columns, toast close buttons and the workflow minimap. Here it takes no
 * page space; the dial pad still opens at the bottom-right when you use it.
 */
function PhoneRow({ collapsed }: { collapsed: boolean }) {
  const open = useCallStore((s) => s.softphoneExpanded)
  const setOpen = useCallStore((s) => s.setSoftphoneExpanded)
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      aria-expanded={open}
      aria-label={open ? 'Close phone' : 'Open phone'}
      data-testid="sidebar-phone"
      title={collapsed ? 'Phone' : undefined}
      className={cn(
        'group relative flex items-center gap-[11px] h-9 coarse:h-11 px-3 rounded-xl text-[13.5px] font-medium transition-colors duration-200 text-left',
        open
          ? 'nav-pill-active text-white shadow-[0_4px_12px_-6px_rgba(0,0,0,0.5)]'
          : 'text-white/[0.66] hover:text-white hover:bg-white/[0.06]',
        collapsed && 'justify-center px-0',
      )}
    >
      <Phone className={cn('w-4 h-4 shrink-0', open ? 'text-white' : 'text-white/[0.62] group-hover:text-white/80')} />
      {!collapsed && <span className="flex-1 truncate">Phone</span>}
      {collapsed && (
        <span className="absolute left-full ml-2 px-2 py-1 glass rounded-md text-xs text-ink-100 whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity pointer-events-none z-[21]">
          Phone
        </span>
      )}
    </button>
  )
}

function UserRow({ collapsed }: { collapsed: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { user, logout } = useAuthStore()
  const { roleLabel } = usePermissions()
  const navigate = useNavigate()
  // Seed/test accounts carry a "[DEMO] " / "[TEST] " prefix; show the real name.
  const name = displayName(user?.name)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Account menu"
        aria-expanded={open}
        className={cn(
          'w-full flex items-center gap-2.5 rounded-xl p-1.5 text-left hover:bg-white/[0.06] transition-colors',
          collapsed && 'justify-center',
        )}
      >
        <Avatar name={name} size="sm" />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold leading-tight truncate text-white">{name}</span>
              <span className="block text-xs leading-tight truncate text-white/[0.62]">
                {roleLabel}
              </span>
            </span>
            <MoreHorizontal className="w-4 h-4 text-white/[0.62] shrink-0" />
          </>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute bottom-full mb-2 w-56 glass-popup py-1 z-50 animate-popover-in',
            collapsed ? 'left-0' : 'left-0 right-0 w-auto',
          )}
        >
          <div className="px-3 py-2 hairline-b mb-1">
            <p className="text-sm font-medium text-ink-100 truncate">{name}</p>
            <p className="text-xs text-white/[0.62] truncate">{user?.email}</p>
          </div>
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] transition-colors"
          >
            <Settings className="w-4 h-4" /> Settings
          </Link>
          <div className="my-1 border-t border-white/[0.06]" />
          <button
            type="button"
            onClick={async () => { setOpen(false); await logout(); navigate('/login', { state: { signedOut: true } }) }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#E1A29A] hover:text-[#EFBFB9] hover:bg-[#A84B3E]/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}

export function Sidebar() {
  const { sidebarCollapsed: collapsed, toggleSidebar, inboxBadge, setSearchOpen } = useUIStore()
  // The tenant's business name (workspace settings); the product name under it.
  const workspace = useWorkspace()

  const { can } = usePermissions()
  const allowed = visibleNavItems(navItems, can)
  const main = allowed.filter((i) => i.group !== 'admin')
  const admin = allowed.filter((i) => i.group === 'admin')
  const badgeFor = (item: NavItem) => (item.path === '/inbox' ? inboxBadge : item.badge ?? 0)

  return (
    <aside
      className={cn(
        // C4: pure MIDNIGHT canvas. I5: transition only width, not every prop.
        'relative z-20 hidden md:flex flex-col h-screen hairline-r',
        'transition-[width] duration-300 ease-smooth shrink-0',
        collapsed ? 'w-16' : 'w-64',
      )}
      style={{ background: 'var(--midnight-bg, #000000)' }}
    >
      {/* Workspace + notifications */}
      <div className={cn('flex items-center gap-2.5 pt-4 pb-1', collapsed ? 'flex-col px-2' : 'pl-4 pr-3')}>
        <WorkspaceMark name={workspace.name} className="w-[30px] h-[30px]" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <BusinessName name={workspace.name} className="block text-[13.5px] font-semibold leading-tight text-white truncate" />
            {workspace.name ? <span className="block text-xs leading-tight tracking-[0.04em] text-white/[0.62]">{PRODUCT_NAME}</span> : null}
          </div>
        )}
        <NotificationDropdown
          align="left"
          className="relative w-8 h-8 coarse:w-11 coarse:h-11 flex items-center justify-center rounded-full text-white/[0.62] hover:text-white hover:bg-white/[0.06] transition-colors [&_svg]:w-4 [&_svg]:h-4 [&>span]:top-1.5 [&>span]:right-1.5"
        />
      </div>

      {/* Search — ⌘K / Ctrl K anywhere */}
      <div className={cn('pt-3 pb-3', collapsed ? 'px-2' : 'px-3')}>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
          title={collapsed ? `Search (${SHORTCUT_LABEL})` : undefined}
          className={cn(
            'w-full flex items-center gap-2 h-9 coarse:h-11 rounded-full border border-white/[0.085] text-white/[0.62] hover:text-white/[0.66] hover:border-white/[0.16] transition-colors',
            'bg-[linear-gradient(180deg,rgba(0,0,0,0.42)_0%,rgba(0,0,0,0.2)_100%)]',
            collapsed ? 'justify-center px-0' : 'pl-3 pr-1.5',
          )}
        >
          <Search className="w-4 h-4 shrink-0" />
          {!collapsed && (
            <>
              <span className="text-[13px]">Search or jump to</span>
              <kbd className="ml-auto font-mono text-xs leading-5 px-1.5 rounded-md border border-white/[0.16] bg-white/[0.06] text-white/[0.62]">
                {SHORTCUT_LABEL}
              </kbd>
            </>
          )}
        </button>
        {/* The in-app assistant (⌘J / Ctrl J) — a panel, not a page. */}
        <div className="mt-1.5">
          <AssistantButton variant="sidebar" collapsed={collapsed} />
        </div>
      </div>

      {/* Nav — a gap between groups instead of divider lines */}
      <nav className={cn('flex-1 overflow-y-auto pb-3 flex flex-col gap-0.5', collapsed ? 'px-2' : 'px-3')} aria-label="Main">
        {main.map((item, i) => (
          <React.Fragment key={item.path}>
            {i > 0 && main[i - 1].group !== item.group && <div className="h-3.5 shrink-0" aria-hidden />}
            <NavRow item={item} collapsed={collapsed} badge={badgeFor(item)} />
            {item.group === 'day' && main[i + 1]?.group !== 'day' && <PhoneRow collapsed={collapsed} />}
          </React.Fragment>
        ))}
      </nav>

      {/* Footer: settings, account, collapse */}
      <div className={cn('pt-2 pb-3 flex flex-col gap-1', collapsed ? 'px-2' : 'px-3')}>
        {admin.map((item) => (
          <NavRow key={item.path} item={item} collapsed={collapsed} badge={0} />
        ))}
        <div className="hairline-t mt-1 pt-2">
          <UserRow collapsed={collapsed} />
        </div>
        <button
          type="button"
          onClick={toggleSidebar}
          className="w-full flex items-center justify-center gap-2 h-8 coarse:h-11 rounded-xl text-white/[0.62] hover:text-ink-100 hover:bg-white/[0.04] transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <ChevronRight className="w-4 h-4" />
            : <><ChevronLeft className="w-4 h-4" /><span className="text-xs">Collapse</span></>
          }
        </button>
      </div>
    </aside>
  )
}
